import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Plus, Trash2, Loader2, Save } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import { useToast } from '../hooks/useToast';
import {
    fetchAllBankAccounts,
    fetchAllBankAccountsNoScope,
    fetchStoresWhereStaffAssigned,
    client
} from '../apiHelper';

const StoreAccountMapping = () => {
    const { showToast } = useToast();

    // Options Data State
    const [bankAccountOptions, setBankAccountOptions] = useState([]);
    const [stores, setStores] = useState([]);

    // Loading States
    const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
    const [loadingStores, setLoadingStores] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Form State: Array of Store Groups, each containing an array of account selections
    const [storeGroups, setStoreGroups] = useState([
        {
            id: Date.now(),
            store_id: '',
            accounts: [{ id: Date.now() + 1, account_id: '' }]
        }
    ]);

    // 1. Fetch Bank Accounts
    useEffect(() => {
        const loadBankAccounts = async () => {
            setLoadingBankAccounts(true);
            try {
                // const fetchedAccounts = await fetchAllBankAccounts();
                const fetchedAccounts = await fetchAllBankAccountsNoScope();
                const formattedOptions = (fetchedAccounts || []).map(account => ({
                    label: `${account.name} (${account.account_id})`,
                    value: account.account_id.toString()
                }));
                setBankAccountOptions(formattedOptions);
            } catch (err) {
                const errorMessage = err.response?.data?.detail || `Failed to load bank accounts: ${err.message || err}`;
                showToast(errorMessage, 'error');
            } finally {
                setLoadingBankAccounts(false);
            }
        };
        loadBankAccounts();
    }, [showToast]);

    // 2. Fetch Stores
    const loadStores = useCallback(async () => {
        setLoadingStores(true);
        try {
            const stored = localStorage.getItem('user');
            const parsed = stored ? JSON.parse(stored) : null;
            if (parsed) {
                const returnedStores = await fetchStoresWhereStaffAssigned(parsed);

                if (returnedStores && returnedStores.length > 0) {
                    const mappedStores = returnedStores.map(store => ({
                        label: store.identifier,
                        value: store.store_id.toString(),
                        currency: store.setccurr,
                    }));
                    setStores(mappedStores);
                }
            }
        } catch (err) {
            console.error("Error loading stores:", err);
            const errorMessage = err.response?.data?.detail || "Failed to load branches.";
            showToast(errorMessage, 'error');
        } finally {
            setLoadingStores(false);
        }
    }, [showToast]);

    useEffect(() => {
        loadStores();
    }, [loadStores]);

    // --- Dynamic Store Group Handlers ---
    const handleAddStoreGroup = () => {
        // Check if the current last group has a selected store before adding a new row
        const currentGroup = storeGroups[storeGroups.length - 1];

        if (currentGroup && !currentGroup.store_id) {
            showToast("Please select a store before adding another mapping.", "error");
            return;
        }

        setStoreGroups(prev => [
            ...prev,
            {
                id: Date.now(),
                store_id: '',
                accounts: [{ id: Date.now() + 1, account_id: '' }]
            }
        ]);
    };

    const handleRemoveStoreGroup = (groupId) => {
        if (storeGroups.length === 1) {
            showToast("At least one store group is required.", "info");
            return;
        }
        setStoreGroups(prev => prev.filter(g => g.id !== groupId));
    };

    const handleStoreChange = (groupId, storeId) => {
        setStoreGroups(prev =>
            prev.map(g => (g.id === groupId ? { ...g, store_id: storeId } : g))
        );
    };

    // --- Dynamic Account Handlers inside a Store Group ---
    const handleAddAccountToStore = (groupId) => {
        setStoreGroups(prev =>
            prev.map(g => {
                if (g.id === groupId) {
                    return {
                        ...g,
                        accounts: [...g.accounts, { id: Date.now(), account_id: '' }]
                    };
                }
                return g;
            })
        );
    };

    const handleRemoveAccountFromStore = (groupId, accountRowId) => {
        setStoreGroups(prev =>
            prev.map(g => {
                if (g.id === groupId) {
                    if (g.accounts.length === 1) {
                        showToast("At least one bank account is required per store.", "info");
                        return g;
                    }
                    return {
                        ...g,
                        accounts: g.accounts.filter(a => a.id !== accountRowId)
                    };
                }
                return g;
            })
        );
    };

    const handleAccountChange = (groupId, accountRowId, value) => {
        setStoreGroups(prev =>
            prev.map(g => {
                if (g.id === groupId) {
                    return {
                        ...g,
                        accounts: g.accounts.map(a =>
                            a.id === accountRowId ? { ...a, account_id: value } : a
                        )
                    };
                }
                return g;
            })
        );
    };

    // --- Form Submission ---
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation & Payload Construction
        const payload = [];

        for (const group of storeGroups) {
            if (!group.store_id) {
                showToast("Please select a store for all configured groups.", "error");
                return;
            }

            const validAccountIds = group.accounts
                .map(a => a.account_id)
                .filter(Boolean);

            if (validAccountIds.length === 0) {
                showToast("Each store must have at least one account selected.", "error");
                return;
            }

            payload.push({
                store_id: Number(group.store_id),
                account_ids: validAccountIds.map(Number)
            });
        }

        setSubmitting(true);
        try {
            console.log(payload)
            const response = await client.postData('/accounting/map-stores-accounts', payload);
            showToast(response?.msg || "Stores accounts mapped successfully!", "success");

            // Reset Form State
            setStoreGroups([
                {
                    id: Date.now(),
                    store_id: '',
                    accounts: [{ id: Date.now() + 1, account_id: '' }]
                }
            ]);
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to save mapping.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-zinc-50 min-h-screen font-sans flex flex-col">
            <Card className="w-full max-w-4xl mx-auto p-6 sm:p-8 shadow-xl rounded-xl bg-white border border-zinc-200">
                {/* Header */}
                <div className="border-b border-zinc-200 pb-5 mb-6">
                    <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
                        <Building2 size={26} className="text-blue-600" /> Map Store Accounts
                    </h1>
                    <p className="text-xs text-zinc-500 mt-1">
                        Assign multiple bank accounts to stores and configure multiple store mappings simultaneously.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Store Groups */}
                    <div className="space-y-6">
                        {storeGroups.map((group, groupIdx) => (
                            <div
                                key={group.id}
                                className="bg-zinc-50 p-5 rounded-xl border border-zinc-200 space-y-4 relative"
                            >
                                {/* Store Selector Header */}
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-zinc-200 pb-3">
                                    <div className="flex-1 max-w-md">
                                        <SearchableSelectBox
                                            label={`Store Location #${groupIdx + 1}`}
                                            options={stores}
                                            value={group.store_id}
                                            onChange={(e) => handleStoreChange(group.id, e.target.value)}
                                            placeholder={loadingStores ? "Loading stores..." : "Select store..."}
                                            required
                                        />
                                    </div>

                                    {storeGroups.length > 1 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleRemoveStoreGroup(group.id)}
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50 self-end sm:self-center flex items-center gap-1 text-xs"
                                        >
                                            <Trash2 size={16} /> Remove Store Block
                                        </Button>
                                    )}
                                </div>

                                {/* Multiple Bank Accounts per Store */}
                                <div className="space-y-3 pt-2">
                                    <div className="flex items-center justify-between">
                                        <label className="text-xs font-bold text-zinc-700 uppercase tracking-wider">
                                            Assigned Bank Accounts
                                        </label>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleAddAccountToStore(group.id)}
                                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 flex items-center gap-1 text-xs font-semibold"
                                        >
                                            <Plus size={16} /> Add Account
                                        </Button>
                                    </div>

                                    <div className="space-y-2">
                                        {group.accounts.map((accRow, accIdx) => (
                                            <div key={accRow.id} className="flex items-center gap-3">
                                                <div className="flex-1">
                                                    <SearchableSelectBox
                                                        options={bankAccountOptions}
                                                        value={accRow.account_id}
                                                        onChange={(e) => handleAccountChange(group.id, accRow.id, e.target.value)}
                                                        placeholder={loadingBankAccounts ? "Loading accounts..." : `Select Account #${accIdx + 1}`}
                                                        required
                                                    />
                                                </div>

                                                {group.accounts.length > 1 && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleRemoveAccountFromStore(group.id, accRow.id)}
                                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2.5 rounded-lg flex-shrink-0"
                                                        title="Remove account"
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Add Another Store Row Button */}
                    <div className="flex justify-start border-t border-zinc-200 pt-4">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={handleAddStoreGroup}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 flex items-center gap-1.5 text-sm font-semibold border border-blue-200"
                        >
                            <Plus size={18} /> Add Another Store Mapping
                        </Button>
                    </div>

                    {/* Submit Action */}
                    <div className="pt-2 flex justify-end">
                        <Button
                            type="submit"
                            disabled={submitting || loadingBankAccounts || loadingStores}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 flex items-center gap-2"
                        >
                            {submitting ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <Save size={16} />
                            )}
                            Save All Mappings
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
};

export default StoreAccountMapping;